import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import RentInclusions from '../../../components/common/RentInclusions/RentInclusions'
import SuiteBreakdownTable, { UnitDataWrapperType } from '../../../components/common/SuiteBreakdownTable/SuiteBreakdownTable'
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer'
import { useDispatch, useSelector } from 'react-redux'
import { deleteIncomeUnitById, getTenantSummaryBySnapshotId, resetIndependentTenantSummaryBySnapshotId, upsertRHOResidential } from '../../../redux/actions/incomeUnit/incomeUnit'
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer'
import { getProfileAttributes, resetGetProfileAttributes } from '../../../redux/actions/profile/profile'
import { GetIndependentLivingState } from '../../../redux/reducers/getIncomeUnit/getIndependentLivingReducer'
import { useReadOnly } from '../../../utils/ReadOnlyContext'
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer'
import { ProfileAttributesState } from '../../../redux/reducers/getProfile/profileAttributes/getAttributesReducer'
import updateProperties from '../../../utils/DataModifier/DataModifier'
import { exactMatcher, getFormErrorByName, useFormErrorContext } from '../../../utils/context/FormErrorContext'
import { useNavigation } from '../../../utils/context/NavigationContext'
import { selectGetIndependentLiving, selectProfileGetAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths'

interface AttributeDataType {
    "independentLivingHeatFlag": boolean | null,
    "independentLivingHydroFlag": boolean | null,
    "independentLivingWaterFlag": boolean | null,
    "independentLivingCableFlag": boolean | null,
    "independentLivingInternetFlag": boolean | null,
    "independentLivingFoodFlag": boolean | null,
    "independentLivingHousekeepingFlag": boolean | null,
    "independentLivingActivityFlag": boolean | null,
    "independentLivingCareServiceFlag": boolean | null,
}
const initialAttributeData: AttributeDataType = {
    "independentLivingHeatFlag": null,
    "independentLivingHydroFlag": null,
    "independentLivingWaterFlag": null,
    "independentLivingCableFlag": null,
    "independentLivingInternetFlag": null,
    "independentLivingFoodFlag": null,
    "independentLivingHousekeepingFlag": null,
    "independentLivingActivityFlag": null,
    "independentLivingCareServiceFlag": null,
}

function IndependentLiving() {
    const { t } = useTranslation(['assisted_living', 'navigation'])
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
    const tenantSummaryState: GetIndependentLivingState = useSelector(selectGetIndependentLiving)
    const saveData: SaveDataState = useSelector(selectSaveData)
    const saveResultState: SaveResultState = useSelector(selectSaveResult)
    const dispatch = useDispatch()
    const [attributeData, setAttributeData] = useState<AttributeDataType>(initialAttributeData)
    const [data, setData] = useState<UnitDataWrapperType[]>([])
    const [isRequestSent, setIsRequestSent] = useState(false)
    const { isReadOnly } = useReadOnly()
    const currentSnapshotId = Number(sessionStorage.getItem("currentSnapshotId") as string)
    const { clearErrors, removeError, errors } = useFormErrorContext()
    const { direction } = useNavigation()
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"

    useEffect(() => {
        if (!isRequestSent) {
            setData([])
            dispatch(getTenantSummaryBySnapshotId(currentSnapshotId, "4"))
            setIsRequestSent(true)
        }
    }, [currentSnapshotId, dispatch, isRequestSent])

    useEffect(() => {
        dispatch(resetGetProfileAttributes())
        dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (tenantSummaryState.loading && data.length > 0) {
            dispatch(resetIndependentTenantSummaryBySnapshotId())
            setData([])
        }
    }, [data.length, tenantSummaryState.loading, dispatch])

    useEffect(() => {
        if (!profileAttributes.loading && profileAttributes.data) {
            const initData = { ...initialAttributeData }
            updateProperties(initData, profileAttributes.data)
            setAttributeData(initData)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileAttributes.data, profileAttributes.loading])

    useEffect(() => {
        if (!tenantSummaryState.loading && tenantSummaryState.success) {
            const respData = tenantSummaryState.tenantSummary as unknown as UnitDataWrapperType[]
            const unitsWithComponentId = respData.map(unit => unit.componentId ? unit : { ...unit, componentId: String(crypto.randomUUID()) })
            const sorted = [...unitsWithComponentId].sort((a, b) => {
                const tenantIdA = Number(a.tenantId) || 0;
                const tenantIdB = Number(b.tenantId) || 0;

                return tenantIdB - tenantIdA
            })
            dispatch(resetIndependentTenantSummaryBySnapshotId())
            setData(sorted)
        }



        // if (isRequestSent && !tenantSummaryState.loading) {
        //     if (tenantSummaryState.success) {
        //         const unitsWithComponentId = tenantSummaryState.tenantSummary.map(unit => unit.componentId ? unit : { ...unit, componentId: crypto.randomUUID()})
        //         setData(unitsWithComponentId as Record<string, string | number | boolean>[])
        //     }
        //     else if (!isReadOnly && !noDataToast) {
        //         toast.warn(t("noDataFound", { ns: 'assisted_living' }) as string)
        //         setNoDataToast(true)
        //     }
        // }
    }, [t, tenantSummaryState, isRequestSent, isReadOnly, dispatch])

    const updateValue = (e: React.ChangeEvent | number | null, index: number, key: string, isNumber?: boolean) => {
        removeError(getFormErrorByName(errors, data[index].componentId as string + "." + key, exactMatcher)?.primaryKey as string)

        const dataSet = [...data]

        if (isNumber) {
            const num = e as number | null | string // why is string here if isNumber?
            dataSet[index] = {
                ...dataSet[index],
                [key]: num
            }
        } else {
            dataSet[index] = {
                ...dataSet[index],
                [key]: ((e as React.ChangeEvent).target as HTMLSelectElement | HTMLInputElement).value
            }
        }
        if (key === "analysisFlag") {
            const analysisReviewedBy = localStorage.getItem("username") ?? ""
            const analysisReviewedDate = (new Date().toISOString()) ?? null
            dataSet[index].analysisReviewedBy = analysisReviewedBy
            dataSet[index].analysisReviewedDate = analysisReviewedDate
        }
        setData(dataSet)
    }

    const deleteRow = (index: number) => {
        const unit = data[index]
        dispatch(deleteIncomeUnitById(currentSnapshotId, String(unit.tenantId)))
    }

    const handleAttributeChange = (name: string, value: string | number | boolean | null) => {
        setAttributeData({
            ...attributeData,
            [name]: value
        })
    }

    useEffect(() => {
        if (saveData.saving && !tenantSummaryState.loading && !profileAttributes.loading) {
            clearErrors()
            const triggerSpectral = isInternal || direction !== "backward"
            const newUnits = data.filter(unit => unit.new === true && unit.unitTypeCode === "4")
            const existingUnits = data.filter(unit => unit.new !== true && unit.unitTypeCode === "4")
            const attrData = { ...profileAttributes.data, ...attributeData } as Record<string, unknown>

            dispatch(upsertRHOResidential(currentSnapshotId, {
                "added": newUnits,
                "updated": existingUnits,
                "property_code": sessionStorage.getItem("propertyCode") as string,
                "spectral_section": profileOverview.profile.profile_type_code + "-independent",
                "trigger_spectral": triggerSpectral,
                "profile_attributes": attrData,
                "is_internal": isInternal
            }))
        }
    }, [attributeData, tenantSummaryState.loading, profileAttributes.loading, currentSnapshotId, dispatch, profileAttributes.data, profileOverview.profile.profile_type_code, saveData.saving, data, clearErrors, direction, isInternal])

    useEffect(() => {
        if (saveResultState.status === 200) {
            setIsRequestSent(false)
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
        }
    }, [currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, saveResultState.status])

    return (
        <>
            <div className='assisted-suite-breakdown-container'>
                <RentInclusions data={{ ...attributeData } as Record<string, boolean | null>}
                    onChange={handleAttributeChange}
                    title="independentLivingHeader" />

                <SuiteBreakdownTable
                    title='independentLivingHeader'
                    data={data}
                    setData={setData}
                    onDelete={deleteRow}
                    onChange={updateValue}
                    unitTypeCode='4'
                    setIsRequestSent={setIsRequestSent}
                    isSuiteTypeMandatory
                    isNumUnitsMandatory
                    isNumBedsMandatory
                    isAreaSFMandatory
                    isActualRentMandatory
                    isMarketRentMandatory
                />
            </div>
        </>
    )
}

export default IndependentLiving